import type { ReactNode } from "react";

/**
 * THE SEGMENTED SINGLE-SELECT of «تنظیمات میز» — a gold-on-felt `radiogroup` of equal buttons, one
 * per option, each carrying its own label (the chess colour / time control / disconnect pickers,
 * تخته نرد's disconnect policy, پاسور's size and شلم's match target).
 *
 * Extracted from `GameSettings`' `ChoiceRow` (card-67) so the شلم SOLO start screen asks for the match
 * target with the SAME control a private host uses — the private screen renders it unchanged. `T` is
 * the stored value: a string key, or a number (شلم's ۶۶۰ / ۱۲۰۰). Controlled: the caller owns the value.
 */
export function SegmentedChoice<T extends string | number>({
  label,
  options,
  value,
  onChange,
  oneLine = false,
  wrapLabels = false,
  compact = false,
  fit = false,
  className = "",
}: {
  /** The group's accessible name — the question it answers. */
  label: string;
  options: readonly (readonly [T, string])[];
  value: T;
  onChange: (value: T) => void;
  /** Keep every choice on ONE line: the chips shrink to fit instead of wrapping into what reads
   *  as two separate groups (chess's five time controls). */
  oneLine?: boolean;
  /** A label wider than its button breaks onto a second line INSIDE it, never past its edge under
   *  the next button (card-98 item 13: chess's «جایگزینی ربات» was cut at text ×1.5 and on Telegram
   *  Desktop's 307×472 — the equal share of the row is narrower than the label there). A label that
   *  fits draws exactly as before. */
  wrapLabels?: boolean;
  /** Start-screen padding — the same step `DifficultySelect`'s `compact` takes, so the rows of a
   *  solo start card match (its height is a measured budget, `StartScreenShell`). */
  compact?: boolean;
  /** Sharing a row with another picker (پاسور's size and target on «تنظیمات میز»): one line, no 70 px
   *  floor under a button, the ordinary text and button height; a label's own width is its floor. */
  fit?: boolean;
  /** Placement classes from the caller (`ChoiceRow`'s top margin). */
  className?: string;
}): ReactNode {
  return (
    <div
      role="radiogroup"
      aria-label={label}
      className={`${className ? `${className} ` : ""}flex gap-1 rounded-lg bg-surface-container-highest ${
        compact ? "p-0.5" : "p-1"
      } ${oneLine || fit ? "flex-nowrap" : "flex-wrap"}`}
    >
      {options.map(([val, text]) => {
        const on = value === val;
        return (
          <button
            key={val}
            type="button"
            role="radio"
            aria-checked={on}
            aria-label={text}
            onClick={() => onChange(val)}
            className={`flex-1 appearance-none cursor-pointer ${
              wrapLabels ? "whitespace-normal" : "whitespace-nowrap"
            } rounded-md border-0 py-2 text-center font-body-md transition-colors ${
              oneLine
                ? "min-w-0 px-1 text-caption"
                : fit
                  ? "min-w-0 px-1 text-body-md"
                  : "min-w-[70px] text-body-md"
            } ${
              on
                ? "bg-gold-deep text-ink-on-gold shadow"
                : "bg-transparent text-on-surface-variant hover:text-on-surface"
            }`}
          >
            {text}
          </button>
        );
      })}
    </div>
  );
}
